import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/inkway`;

// Scoped axios instance for Inkway — same-origin cookies sent automatically
const inkwayAxios = axios.create();

const InkwayAuthContext = createContext(null);

export function InkwayAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const formatError = (detail, fallback) => {
    if (detail == null) return fallback || "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
    return String(detail);
  };

  const checkAuth = useCallback(async () => {
    // Only probe Inkway auth on Inkway routes — everywhere else (marketing
    // pages, portals) the probe just produces a guaranteed 401 in the console.
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/inkway')) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await inkwayAxios.get(`${API}/auth/me`);
      setUser(data);
    } catch (e) {
      console.error('Inkway auth check failed:', e.message);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await inkwayAxios.post(`${API}/auth/login`, { email, password });
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      console.error('Inkway login failed:', e.message);
      return { success: false, error: formatError(e.response?.data?.detail, e.message) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await inkwayAxios.post(`${API}/auth/register`, { email, password, name });
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      console.error('Inkway register failed:', e.message);
      return { success: false, error: formatError(e.response?.data?.detail, e.message) };
    }
  };

  const logout = async () => {
    try { await inkwayAxios.post(`${API}/auth/logout`); } catch (e) { console.error('Logout error:', e.message); }
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      await inkwayAxios.post(`${API}/auth/refresh`);
      await checkAuth();
    } catch (e) {
      console.error('Inkway token refresh failed:', e.message);
      setUser(false);
    }
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  return (
    <InkwayAuthContext.Provider value={{
      user, loading, login, register, logout, refreshToken, updateUser,
      isAuthenticated: !!user && user !== false
    }}>
      {children}
    </InkwayAuthContext.Provider>
  );
}

export function useInkwayAuth() {
  const context = useContext(InkwayAuthContext);
  if (!context) throw new Error('useInkwayAuth must be used within InkwayAuthProvider');
  return context;
}
